// Constants for PracticeTest component
import * as questionData from '../../question_data';
import type { Question } from '../../types';

/** Number of questions per practice test. */
export const QUESTIONS_PER_TEST = 10;
/** Each test must have at least this many questions (top up rest if needed). */
export const MIN_QUESTIONS_PER_TEST = 10;
/** Maximum questions per test. */
export const MAX_QUESTIONS_PER_TEST = 10;

// Map test IDs to question loaders
// CBR 2026 TIER 1: CRITICAL (8 topics)
const roadMarkingsLoader = () => questionData.roadMarkingsQuestions;
const trafficSignsLoader = () => [...questionData.mandatorySignsQuestions, ...questionData.prohibitorySignsQuestions, ...questionData.signIdentificationQuestions];
const motorwayMergingLoader = () => questionData.motorwayRulesQuestions;
const riskAwarenessLoader = () => questionData.riskAwarenessQuestions;

// CBR 2026 TIER 2: IMPORTANT (9 topics)
const incidentFirstAidLoader = () => questionData.incidentFirstAidQuestions;
const vehicleCategoriesLoader = () => questionData.vehicleCategoriesQuestions;
const technologySafetyLoader = () => questionData.technologySafetyQuestions;
const vehicleDocumentationLoader = () => questionData.vehicleDocumentationQuestions;

// CBR 2026 TIER 3: ENHANCEMENT (8 topics) - Now using consolidated files
const priorityRulesLoader = () => questionData.priorityRulesQuestions;
const mandatorySignsLoader = () => questionData.mandatorySignsQuestions;
const prohibitorySignsLoader = () => questionData.prohibitorySignsQuestions;

export const TEST_QUESTION_MAP: Record<string, () => Question[]> = {
  // CBR 2026 TIER 1: CRITICAL
  'road-markings': roadMarkingsLoader,
  'motorway-rules': () => questionData.motorwayRulesQuestions,
  'traffic-signs': trafficSignsLoader,
  'vehicle-handling': () => questionData.vehicleHandlingQuestions,
  'tram-interactions': () => questionData.tramInteractionsQuestions,
  'motorway-merging': motorwayMergingLoader,
  'risk-awareness': riskAwarenessLoader,
  'night-visibility': () => questionData.nightVisibilityQuestions,

  // CBR 2026 TIER 2: IMPORTANT
  'weather-conditions': () => questionData.weatherConditionsQuestions,
  'incident-first-aid': incidentFirstAidLoader,
  'pedestrian-crossings': () => questionData.pedestrianCrossingsQuestions,
  'vehicle-categories': vehicleCategoriesLoader,
  'construction-zones': () => questionData.constructionZonesQuestions,
  'technology-safety': technologySafetyLoader,
  'fatigue-rest': () => questionData.fatigueRestQuestions,
  'environmental-zones': () => questionData.environmentalZonesQuestions,
  'vehicle-documentation': vehicleDocumentationLoader,

  // CBR 2026 TIER 3: ENHANCEMENT (using consolidated files)
  'priority-rules': priorityRulesLoader,
  'mandatory-signs': mandatorySignsLoader,
  'traffic-lights-signals': () => questionData.trafficLightsSignalsQuestions,
  'prohibitory-signs': prohibitorySignsLoader,
  'roundabout-rules': () => questionData.roundaboutRulesQuestions,
  'emergency-procedures': () => questionData.emergencyProceduresQuestions,
  'alcohol-drugs': () => questionData.alcoholDrugsQuestions,

  'specialized-driving': () => questionData.specializedDrivingQuestions,
  // BONUS
  'adas-safety': () => questionData.adasSafetyQuestions,

  // BACKWARD COMPAT (old test IDs → CBR 2026 topics)
  'traffic-lights': () => questionData.trafficLightsSignalsQuestions,
  'hazard-perception': riskAwarenessLoader,
  'speed-limits': motorwayMergingLoader,
  'speed-safety': motorwayMergingLoader,
  'bicycle-interactions': () => questionData.pedestrianCrossingsQuestions,
  'road-signs': trafficSignsLoader,
  'road-information': trafficSignsLoader,
  'vehicle-knowledge': vehicleCategoriesLoader,
  'parking-rules': vehicleDocumentationLoader,
  'environmental': () => questionData.environmentalZonesQuestions,
  'insight-practice': priorityRulesLoader,
  'safety-rules': technologySafetyLoader,
  'lane-changing': () => questionData.motorwayRulesQuestions,
  'overtaking': () => questionData.motorwayRulesQuestions,
};
